import React, { useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { ShoppingBag, Package, Loader2 } from 'lucide-react';
import { CategoryCombobox } from '../CategoryCombobox';
import { ImageWithFallback } from '../ui/image-with-fallback';

/**
 * Create/Edit service dialog with tier-pricing rows, image picker, and tags
 * input. Heavy lifting (image compression, upload, payload assembly) lives
 * in the parent `handleSave`.
 */
export function ServiceFormDialog({
  open, onOpenChange, editingService, form, setForm,
  imagePreview, setImagePreview, setImageFile,
  plans, saving, onSave, api,
}) {
  const imageInputRef = useRef(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#041A3B] border-white/10 max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-[#00E676]" />
            {editingService ? 'Edit Service' : 'Add New Service'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-white">Service Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Premium Haircut" className="bg-[#000B18] border-white/20 text-white" data-testid="service-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-white">Description *</Label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Describe your service..." rows={3}
              className="w-full px-3 py-2 rounded-md bg-[#000B18] border border-white/20 text-white text-sm placeholder:text-[#5C7599] focus:border-[#00E5FF] focus:outline-none resize-none"
              data-testid="service-desc-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-white">Type *</Label>
              <select value={form.item_type} onChange={(e) => setForm({ ...form, item_type: e.target.value, category_id: '' })}
                className="w-full h-10 px-3 rounded-md bg-[#000B18] border border-white/20 text-white text-sm focus:border-[#00E5FF] focus:outline-none"
                data-testid="service-item-type">
                <option value="service">Service</option>
                <option value="product">Product</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-white">Category</Label>
              <CategoryCombobox
                api={api}
                itemType={form.item_type || 'service'}
                value={form.category_id}
                onChange={(catId, catName) => setForm({ ...form, category_id: catId, category: catName || form.category })}
                placeholder="Pick or search a category…"
                testId="service-category-input"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-white">Regular Price (PHP) *</Label>
            <Input type="number" value={form.regular_price} onChange={(e) => setForm({ ...form, regular_price: e.target.value })}
              placeholder="0.00" className="bg-[#000B18] border-white/20 text-white" data-testid="service-regular-price" />
          </div>
          {plans.length > 0 && (
            <div className="space-y-2">
              <Label className="text-white text-sm">Tier Pricing (Discounted Price per Plan)</Label>
              <p className="text-[#5C7599] text-[10px]">Set a discounted price for each subscription tier. Leave blank to use regular price.</p>
              <div className="space-y-2">
                {plans.map((plan) => {
                  const tierVal = form.tier_prices?.[plan.id] || '';
                  const rp = parseFloat(form.regular_price) || 0;
                  const tp = parseFloat(tierVal) || 0;
                  const disc = rp > 0 && tp > 0 && tp < rp ? Math.round(((rp - tp) / rp) * 100) : 0;
                  return (
                    <div key={plan.id} className="flex items-center gap-2">
                      <div className="w-28 flex-shrink-0">
                        <span className="text-[#A0B4CC] text-xs font-medium">{plan.name}</span>
                      </div>
                      <Input
                        type="number"
                        value={tierVal}
                        onChange={(e) => setForm({ ...form, tier_prices: { ...form.tier_prices, [plan.id]: e.target.value } })}
                        placeholder={form.regular_price || '0.00'}
                        className="bg-[#000B18] border-white/20 text-white h-9 text-sm flex-1"
                        data-testid={`tier-price-${plan.id}`}
                      />
                      {disc > 0 && <span className="text-[#00E676] text-[10px] font-medium w-12 flex-shrink-0">{disc}% off</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-[#A0B4CC] text-xs">Service / Product Image</Label>
              <span className="text-[10px] text-[#5C7599]">Recommended: 1200×800 px · JPG/PNG · auto-compressed</span>
            </div>
            <input type="file" ref={imageInputRef} accept="image/*" onChange={(e) => {
              const f = e.target.files[0];
              if (f) { setImageFile(f); setImagePreview(URL.createObjectURL(f)); }
            }} className="hidden" />
            {imagePreview ? (
              <div className="relative rounded-lg overflow-hidden border border-white/20">
                <ImageWithFallback
                  src={imagePreview}
                  alt="Service"
                  className="w-full h-28 object-cover"
                  fallback={
                    <div className="w-full h-28 flex flex-col items-center justify-center gap-1.5 bg-[#000B18] text-[#5C7599]">
                      <Package className="w-6 h-6" />
                      <span className="text-xs">Image preview unavailable</span>
                    </div>
                  }
                />
                <button onClick={() => { setImageFile(null); setImagePreview(null); setForm({ ...form, image_url: '' }); }}
                  className="absolute top-2 right-2 bg-[#FF1744] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">X</button>
              </div>
            ) : (
              <button type="button" onClick={() => imageInputRef.current?.click()} data-testid="upload-service-image-btn"
                className="w-full h-24 rounded-lg border-2 border-dashed border-white/20 hover:border-[#00E5FF]/50 transition-colors flex flex-col items-center justify-center gap-1.5">
                <Package className="w-6 h-6 text-[#5C7599]" />
                <span className="text-[#A0B4CC] text-xs">Upload product / service image</span>
              </button>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#A0B4CC] text-xs">Tags (comma separated)</Label>
            <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })}
              placeholder="haircut, salon, beauty" className="bg-[#000B18] border-white/20 text-white text-sm" />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-[#A0B4CC]">Cancel</Button>
          <Button onClick={onSave} disabled={saving} className="bg-[#00E676] text-[#000B18] hover:bg-[#00E676]/90" data-testid="save-service-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingService ? 'Update Service' : 'Create Service'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
